import React, { useState, useEffect, useCallback } from "react";
import { DS } from "../theme";
import { changePassword, getWalletTransactions, getUserPurchases, createSubscriptionSession, getConnectOnboardUrl, getConnectStatus, getUserGames, redeemPromo, getGameMoves } from "../lib/api";
import { getInitialGame, applyMove, finalizeMove, getPieceAt, isWP } from "../chess/engine";
import { getPieceStyle } from "../data/shopItems";

const SECTION_TABS = [
  ["profile",    "♟  Profile"],
  ["appearance", "◈  Appearance"],
  ["sound",      "♪  Sound"],
  ["passes",     "♕  Passes"],
  ["wallet",     "◆  Wallet"],
  ["history",    "⚔  History"],
];

export default function AccountSettings({ user, prefs, onPrefsChange, onLogout, onCrownsUpdate }) {
  const [section, setSection] = useState("profile");

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      {/* Section tabs */}
      <div style={{ display: "flex", background: DS.elevated, borderRadius: 10, padding: 4, marginBottom: 20, gap: 4 }}>
        {SECTION_TABS.map(([t, lbl]) => (
          <button key={t} onClick={() => setSection(t)} style={{
            flex: 1, padding: "8px 0", border: "none", borderRadius: 8,
            cursor: "pointer", fontFamily: "'Cinzel', Georgia, serif",
            fontWeight: 600, fontSize: 11, letterSpacing: "0.06em",
            transition: "all 0.15s",
            background: section === t ? DS.accent : "transparent",
            color: section === t ? "#fff" : DS.textMuted,
            boxShadow: section === t ? "0 2px 8px rgba(115,102,240,0.3)" : "none",
          }}>{lbl}</button>
        ))}
      </div>

      {section === "profile"    && <ProfileSection    user={user} onLogout={onLogout} />}
      {section === "appearance" && <AppearanceSection prefs={prefs} onChange={onPrefsChange} />}
      {section === "sound"      && <SoundSection      prefs={prefs} onChange={onPrefsChange} />}
      {section === "passes"     && <PassesSection     user={user} />}
      {section === "wallet"     && <WalletSection     user={user} onCrownsUpdate={onCrownsUpdate} />}
      {section === "history"    && <HistorySection    user={user} />}
    </div>
  );
}

// ── PROFILE ──────────────────────────────────────────────────────────────────

function ProfileSection({ user, onLogout }) {
  const [currentPw, setCurrentPw]   = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [pwMsg, setPwMsg]           = useState(null); // { text, ok }
  const [pwLoading, setPwLoading]   = useState(false);

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwMsg({ text: "New passwords don't match.", ok: false }); return; }
    if (newPw.length < 8)   { setPwMsg({ text: "Password must be at least 8 characters.", ok: false }); return; }
    setPwLoading(true);
    setPwMsg(null);
    try {
      const data = await changePassword(user?.token, currentPw, newPw);
      if (data.error) { setPwMsg({ text: data.error, ok: false }); }
      else            { setPwMsg({ text: "Password updated successfully.", ok: true }); setCurrentPw(""); setNewPw(""); setConfirmPw(""); }
    } catch {
      setPwMsg({ text: "Could not reach server — try again.", ok: false });
    }
    setPwLoading(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Account info */}
      <div style={card}>
        <div style={sLabel}>Account Info</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <InfoField label="Username" value={user?.username || "—"} />
          <InfoField label="User ID"  value={user?.userId   ? `#${String(user.userId).slice(-6)}` : "—"} />
        </div>
      </div>

      {/* Change password */}
      <div style={card}>
        <div style={sLabel}>Change Password</div>
        <form onSubmit={handleChangePassword} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input type="password" placeholder="Current password" value={currentPw} required
            onChange={e => setCurrentPw(e.target.value)} style={inputStyle} />
          <input type="password" placeholder="New password (min 8 chars)" value={newPw} required
            onChange={e => setNewPw(e.target.value)} style={inputStyle} />
          <input type="password" placeholder="Confirm new password" value={confirmPw} required
            onChange={e => setConfirmPw(e.target.value)} style={inputStyle} />
          {pwMsg && (
            <div style={{
              fontSize: 12, padding: "7px 10px", borderRadius: 6,
              color: pwMsg.ok ? DS.success : DS.danger,
              background: pwMsg.ok ? "rgba(75,184,122,0.1)" : "rgba(224,70,70,0.1)",
            }}>{pwMsg.text}</div>
          )}
          <button type="submit" className="btn btn-primary btn-sm cinzel" disabled={pwLoading}
            style={{ alignSelf: "flex-start", letterSpacing: "0.06em" }}>
            {pwLoading ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>

      {/* Sign out */}
      <div style={card}>
        <div style={sLabel}>Session</div>
        <p style={{ fontSize: 12, color: DS.textMuted, marginBottom: 14, lineHeight: 1.6 }}>
          Signing out will clear your session. Your stats and trophies are saved locally and will still be here when you return.
        </p>
        <button className="btn btn-danger cinzel" onClick={onLogout}
          style={{ fontSize: 12, letterSpacing: "0.06em" }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div style={{ background: DS.elevated, borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, color: DS.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, fontFamily: "'Cinzel', serif" }}>{label}</div>
      <div style={{ fontSize: 13, color: DS.text, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

// ── APPEARANCE ────────────────────────────────────────────────────────────────

const BOARD_SIZES = [
  { id: "compact",  label: "Compact",  desc: "58px squares — fits small screens" },
  { id: "standard", label: "Standard", desc: "68px squares — default" },
  { id: "large",    label: "Large",    desc: "78px squares — desktop full-screen" },
];

const FONT_SCALES = [
  { id: 0.85, label: "Small",  sample: 11 },
  { id: 1.0,  label: "Medium", sample: 13 },
  { id: 1.15, label: "Large",  sample: 15 },
];

const ORIENTATIONS = [
  { id: "auto",      label: "Auto",      icon: "⟳", desc: "Follow device" },
  { id: "landscape", label: "Landscape", icon: "⬌", desc: "Lock horizontal" },
  { id: "portrait",  label: "Portrait",  icon: "⬍", desc: "Lock vertical" },
];

function AppearanceSection({ prefs, onChange }) {
  function lockOrientation(id) {
    onChange({ orientation: id });
    try {
      const orient = window.screen?.orientation;
      if (!orient) return;
      if (id === "auto")           orient.unlock();
      else if (id === "landscape") orient.lock("landscape").catch(() => {});
      else if (id === "portrait")  orient.lock("portrait").catch(() => {});
    } catch {}
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Board size */}
      <div style={card}>
        <div style={sLabel}>Board Size</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {BOARD_SIZES.map(s => {
            const active = prefs.boardSize === s.id;
            return (
              <div key={s.id} onClick={() => onChange({ boardSize: s.id })} style={{
                background: active ? `rgba(115,102,240,0.12)` : DS.elevated,
                border: `2px solid ${active ? DS.accent : DS.borderFaint}`,
                borderRadius: 10, padding: "12px 10px", cursor: "pointer", textAlign: "center",
                transition: "all 0.15s",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: active ? DS.accent : DS.text, marginBottom: 4, fontFamily: "'Cinzel', serif" }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 10, color: DS.textMuted, lineHeight: 1.4 }}>{s.desc}</div>
                {active && <div className="cinzel" style={{ fontSize: 10, color: DS.accent, marginTop: 6 }}>✓ Active</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Text size */}
      <div style={card}>
        <div style={sLabel}>Text Size</div>
        <div style={{ display: "flex", gap: 10 }}>
          {FONT_SCALES.map(f => {
            const active = prefs.fontScale === f.id;
            return (
              <div key={f.id} onClick={() => onChange({ fontScale: f.id })} style={{
                flex: 1, background: active ? `rgba(115,102,240,0.12)` : DS.elevated,
                border: `2px solid ${active ? DS.accent : DS.borderFaint}`,
                borderRadius: 10, padding: "14px 10px", cursor: "pointer", textAlign: "center",
                transition: "all 0.15s",
              }}>
                <div style={{ fontSize: f.sample, fontWeight: 700, color: active ? DS.accent : DS.text, marginBottom: 4 }}>Aa</div>
                <div className="cinzel" style={{ fontSize: 10, color: DS.textMuted }}>{f.label}</div>
                {active && <div className="cinzel" style={{ fontSize: 10, color: DS.accent, marginTop: 4 }}>✓</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Screen orientation */}
      <div style={card}>
        <div style={sLabel}>Screen Orientation</div>
        <div style={{ fontSize: 11, color: DS.textMuted, marginBottom: 12, lineHeight: 1.6 }}>
          Lock the screen direction when playing on mobile or tablet.
          Desktop browsers ignore this setting.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {ORIENTATIONS.map(o => {
            const active = prefs.orientation === o.id;
            return (
              <div key={o.id} onClick={() => lockOrientation(o.id)} style={{
                flex: 1, background: active ? `rgba(115,102,240,0.12)` : DS.elevated,
                border: `2px solid ${active ? DS.accent : DS.borderFaint}`,
                borderRadius: 10, padding: "12px 10px", cursor: "pointer", textAlign: "center",
                transition: "all 0.15s",
              }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{o.icon}</div>
                <div className="cinzel" style={{ fontSize: 10, fontWeight: 700, color: active ? DS.accent : DS.text }}>{o.label}</div>
                <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 3 }}>{o.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── SOUND ─────────────────────────────────────────────────────────────────────

function SoundSection({ prefs, onChange }) {
  const sfxOn      = prefs.sfxOn      ?? true;
  const sfxVol     = prefs.sfxVol     ?? 0.7;
  const ambientOn  = prefs.ambientOn  ?? false;
  const ambientVol = prefs.ambientVol ?? 0.32;
  const ambientSong = prefs.ambientSong ?? 0;

  const songs = [
    { desc: "Slow jazz ballad — pads, bass, piano melody" },
    { desc: "Medium swing — sax lead, piano comp, brushed kit" },
    { desc: "Bossa nova — nylon guitar melody, rimshot groove" },
    { desc: "Solo piano — arpeggios, no drums" },
    { desc: "Ambient — slow pads with bell tones" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Sound Effects */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={sLabel}>Sound Effects</div>
          <Toggle on={sfxOn} onToggle={() => onChange({ sfxOn: !sfxOn })} />
        </div>
        <div style={{ opacity: sfxOn ? 1 : 0.35, transition: "opacity 0.2s" }}>
          <div style={{ fontSize: 11, color: DS.textFaint, marginBottom: 8 }}>
            Move clicks, captures, check alerts, game over
          </div>
          <VolumeSlider value={sfxVol} onChange={v => onChange({ sfxVol: v })} disabled={!sfxOn} />
        </div>
      </div>

      {/* Ambient Music */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={sLabel}>Ambient Music</div>
          <Toggle on={ambientOn} onToggle={() => onChange({ ambientOn: !ambientOn })} />
        </div>
        <div style={{ opacity: ambientOn ? 1 : 0.35, transition: "opacity 0.2s" }}>
          <VolumeSlider value={ambientVol} onChange={v => onChange({ ambientVol: v })} disabled={!ambientOn} />
        </div>

        {/* Song selector */}
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 10, color: DS.textFaint, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Now Playing</div>
          {["Blue Dusk", "Midnight Swing", "Café Bossa", "Rain on Keys", "Ember Hours"].map((name, idx) => (
            <button key={idx}
              onClick={() => onChange({ ambientSong: idx })}
              style={{
                background: ambientSong === idx ? `${DS.accent}18` : DS.elevated,
                border: `1px solid ${ambientSong === idx ? DS.accent + "50" : DS.borderFaint}`,
                borderRadius: 8, padding: "10px 14px", cursor: "pointer",
                textAlign: "left", transition: "all 0.15s",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
                  border: `2px solid ${ambientSong === idx ? DS.accent : DS.borderFaint}`,
                  background: ambientSong === idx ? DS.accent : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {ambientSong === idx && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />}
                </div>
                <div>
                  <div className="cinzel" style={{ fontSize: 11, fontWeight: 700, color: ambientSong === idx ? DS.text : DS.textMuted }}>
                    {name}
                  </div>
                  <div style={{ fontSize: 10, color: DS.textFaint, marginTop: 2 }}>{songs[idx].desc}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}

function Toggle({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
        background: on ? DS.accent : DS.elevated,
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        position: "absolute", top: 3,
        left: on ? 23 : 3,
        transition: "left 0.2s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
      }} />
    </button>
  );
}

function VolumeSlider({ value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 13, color: DS.textFaint }}>🔈</span>
      <input
        type="range" min="0" max="1" step="0.01"
        value={value}
        disabled={disabled}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: DS.accent, cursor: disabled ? "default" : "pointer" }}
      />
      <span style={{ fontSize: 13, color: DS.textFaint }}>🔊</span>
      <span style={{ fontSize: 11, color: DS.textMuted, width: 32, textAlign: "right" }}>
        {Math.round(value * 100)}%
      </span>
    </div>
  );
}

// ── WALLET ────────────────────────────────────────────────────────────────────

// ── PASSES ────────────────────────────────────────────────────────────────────

function PassesSection({ user }) {
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading]     = useState(false);
  const [subError, setSubError]         = useState(null);

  const hasSub = subscription?.status === "active";
  const tier   = subscription?.tier;

  useEffect(() => {
    if (!user?.token) return;
    getUserPurchases(user.token).then(data => {
      if (data.subscription) setSubscription(data.subscription);
    }).catch(() => {});
  }, [user]);

  async function handleSubscribe(t) {
    if (!user?.token) return;
    setSubLoading(t);
    setSubError(null);
    try {
      const data = await createSubscriptionSession(user.token, t);
      if (data.url) { window.location.href = data.url; return; }
      if (data.error) setSubError(data.error);
    } catch {
      setSubError("Could not reach server. Please try again.");
    }
    setSubLoading(false);
  }

  const card = { background: DS.surface, border: `1px solid ${DS.borderFaint}`, borderRadius: DS.radius, padding: "20px 22px", marginBottom: 14 };
  const lbl  = { fontFamily: "'Cinzel', Georgia, serif", fontSize: 10, fontWeight: 700, color: DS.textFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 };

  if (hasSub) {
    return (
      <div>
        <div style={lbl}>Your Pass</div>
        <div style={{ ...card, background: "rgba(75,184,122,0.06)", border: "1px solid rgba(75,184,122,0.2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 22, color: tier === "pro" ? DS.gold : DS.success }}>♕</span>
            <div>
              <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, color: DS.success, letterSpacing: "0.06em" }}>
                {tier === "pro" ? "Pro Pass" : "Arena Pass"} — Active
              </div>
              {subscription.current_period_end && (
                <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 3 }}>
                  Renews {new Date(subscription.current_period_end).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.6 }}>
            {tier === "pro"
              ? "Unlimited BLANCHE · Your name displays in gold · Monthly drop item"
              : "10 BLANCHE analyses/month · Monthly drop item"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={lbl}>Passes &amp; Subscriptions</div>

      {subError && (
        <div style={{ fontSize: 12, color: DS.danger, background: "rgba(224,70,70,0.08)", border: "1px solid rgba(224,70,70,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>{subError}</div>
      )}

      {/* Arena */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="cinzel" style={{ fontSize: 12, fontWeight: 700, color: DS.gold, letterSpacing: "0.06em", marginBottom: 4 }}>♕ Arena Pass</div>
            <div className="cinzel" style={{ fontSize: 20, fontWeight: 700, color: DS.text }}>$4.99<span style={{ fontSize: 11, color: DS.textMuted, fontWeight: 400 }}>/mo</span></div>
          </div>
        </div>
        {[
          "10 BLANCHE coaching sessions per month",
          "1 exclusive item drop per month (board, colour, or design)",
          "14-day free trial",
        ].map(f => (
          <div key={f} style={{ display: "flex", gap: 8, fontSize: 12, color: DS.textMuted, marginBottom: 6 }}>
            <span style={{ color: DS.gold, flexShrink: 0 }}>✓</span>{f}
          </div>
        ))}
        <button className="btn btn-gold cinzel" style={{ width: "100%", fontSize: 11, letterSpacing: "0.06em", marginTop: 14 }}
          onClick={() => handleSubscribe("arena")} disabled={subLoading === "arena"}>
          {subLoading === "arena" ? "Loading…" : "Start Free Trial"}
        </button>
      </div>

      {/* Pro */}
      <div style={{ ...card, background: `linear-gradient(160deg, ${DS.accent}12, ${DS.elevated})`, border: `2px solid ${DS.accent}40`, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, right: 0, background: DS.accent, color: "#fff", fontSize: 9, fontWeight: 700, padding: "4px 10px", borderBottomLeftRadius: 8, letterSpacing: "0.08em" }} className="cinzel">BEST VALUE</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="cinzel" style={{ fontSize: 12, fontWeight: 700, color: DS.accent, letterSpacing: "0.06em", marginBottom: 4 }}>♛ Pro Pass</div>
            <div className="cinzel" style={{ fontSize: 20, fontWeight: 700, color: DS.text }}>$9.99<span style={{ fontSize: 11, color: DS.textMuted, fontWeight: 400 }}>/mo</span></div>
          </div>
        </div>
        {[
          "Unlimited BLANCHE coaching",
          "1 exclusive item drop per month",
          "Your username displays in metallic gold",
          "Full game memory and pattern tracking",
          "Live move commentary",
          "14-day free trial",
        ].map(f => (
          <div key={f} style={{ display: "flex", gap: 8, fontSize: 12, color: DS.textMuted, marginBottom: 6 }}>
            <span style={{ color: DS.accent, flexShrink: 0 }}>✓</span>{f}
          </div>
        ))}
        <button className="btn btn-primary cinzel" style={{ width: "100%", fontSize: 11, letterSpacing: "0.06em", marginTop: 14 }}
          onClick={() => handleSubscribe("pro")} disabled={subLoading === "pro"}>
          {subLoading === "pro" ? "Loading…" : "Start Free Trial"}
        </button>
      </div>

      <div style={{ fontSize: 11, color: DS.textFaint, textAlign: "center", lineHeight: 1.7 }}>
        Items from passes go to your Armoury. No charge for 14 days.
      </div>
    </div>
  );
}

// ── WALLET ────────────────────────────────────────────────────────────────────

function WalletSection({ user, onCrownsUpdate }) {
  const [subscription, setSubscription]   = useState(null);
  const [transactions, setTransactions]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [subLoading, setSubLoading]       = useState(false);
  const [msg, setMsg]                     = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [promoCode, setPromoCode]   = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMsg, setPromoMsg]     = useState(null);

  useEffect(() => {
    if (!user?.token) { setLoading(false); return; }
    Promise.all([
      getUserPurchases(user.token),
      getWalletTransactions(user.token),
      getConnectStatus(user.token),
    ]).then(([purchaseData, txns, cs]) => {
      if (purchaseData.subscription) setSubscription(purchaseData.subscription);
      if (Array.isArray(txns)) setTransactions(txns);
      if (cs) setConnectStatus(cs);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'success' && user?.token) {
      window.history.replaceState({}, '', window.location.pathname);
      getConnectStatus(user.token).then(s => setConnectStatus(s)).catch(() => {});
    }
  }, []); // eslint-disable-line

  async function handleConnect() {
    if (!user?.token) return;
    setConnectLoading(true);
    try {
      const data = await getConnectOnboardUrl(user.token);
      if (data.url) window.location.href = data.url;
    } catch {}
    setConnectLoading(false);
  }

  async function handleSubscribe() {
    if (!user?.token) return;
    setSubLoading(true);
    setMsg(null);
    try {
      const data = await createSubscriptionSession(user.token);
      if (data.url) { window.location.href = data.url; return; }
      setMsg({ text: data.error || "Subscription not yet configured — add Stripe keys to server.", ok: false });
    } catch {
      setMsg({ text: "Could not reach server.", ok: false });
    }
    setSubLoading(false);
  }

  async function handlePromo() {
    if (!promoCode.trim() || promoLoading) return;
    setPromoLoading(true);
    setPromoMsg(null);
    try {
      const data = await redeemPromo(user.token, promoCode);
      if (data.success) {
        if (data.crowns) {
          setPromoMsg({ ok: true, text: `👑 ${data.crowns.toLocaleString()} crowns added to your balance!` });
          onCrownsUpdate?.(data.newBalance);
        } else {
          setPromoMsg({ ok: true, text: `Code applied! ${data.tier === 'pro' ? 'Pro Pass' : 'Arena Pass'} active until ${new Date(data.expiresAt).toLocaleDateString()}.` });
          setSubscription({ status: 'active', current_period_end: data.expiresAt });
        }
        setPromoCode("");
      } else {
        setPromoMsg({ ok: false, text: data.error || "Invalid code." });
      }
    } catch {
      setPromoMsg({ ok: false, text: "Could not reach server." });
    }
    setPromoLoading(false);
  }

  const hasSub = subscription?.status === "active";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Arena Pass card */}
      <div style={{
        ...card,
        background: hasSub
          ? "rgba(75,184,122,0.07)"
          : `linear-gradient(135deg, rgba(237,187,74,0.08), rgba(115,102,240,0.08))`,
        border: `1px solid ${hasSub ? "rgba(75,184,122,0.25)" : "rgba(237,187,74,0.2)"}`,
      }}>
        <div style={sLabel}>Arena Pass</div>

        {loading ? (
          <div style={{ fontSize: 13, color: DS.textMuted }}>Loading…</div>
        ) : hasSub ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 28, color: DS.gold }}>♕</span>
              <div>
                <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, color: DS.success }}>Active</div>
                {subscription?.current_period_end && (
                  <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 2 }}>
                    Renews {new Date(subscription.current_period_end).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.6 }}>
              All paid armoury items are unlocked. Manage or cancel your subscription from the{" "}
              <strong style={{ color: DS.text }}>Stripe customer portal</strong>.
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: DS.text, marginBottom: 8 }}>
              $4.99 <span style={{ fontSize: 11, color: DS.textMuted, fontWeight: 400 }}>/ month</span>
            </div>
            <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
              Unlocks <strong style={{ color: DS.text }}>all</strong> paid boards, colour packs, and piece designs.
              Cancel anytime.
            </div>
            {msg && (
              <div style={{
                fontSize: 12, padding: "7px 10px", borderRadius: 6, marginBottom: 12,
                color: msg.ok ? DS.success : DS.warning,
                background: msg.ok ? "rgba(75,184,122,0.1)" : "rgba(232,160,48,0.1)",
              }}>{msg.text}</div>
            )}
            <button className="btn btn-gold cinzel"
              style={{ fontSize: 11, letterSpacing: "0.06em" }}
              onClick={handleSubscribe} disabled={subLoading}>
              {subLoading ? "Loading…" : "Subscribe — $4.99/mo"}
            </button>
          </div>
        )}
      </div>

      {/* Promo code */}
      <div style={card}>
        <div style={sLabel}>Promo Code</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={promoCode}
            onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoMsg(null); }}
            onKeyDown={e => e.key === "Enter" && handlePromo()}
            placeholder="Enter code…"
            maxLength={32}
            className="cinzel"
            style={{
              flex: 1, background: DS.elevated, border: `1px solid ${DS.border}`,
              borderRadius: 8, padding: "9px 12px", color: DS.text,
              fontSize: 12, outline: "none", letterSpacing: "0.06em",
            }}
          />
          <button
            className="btn btn-primary cinzel"
            style={{ fontSize: 11, letterSpacing: "0.06em", flexShrink: 0 }}
            onClick={handlePromo}
            disabled={promoLoading || !promoCode.trim()}
          >
            {promoLoading ? "…" : "Redeem"}
          </button>
        </div>
        {promoMsg && (
          <div style={{
            marginTop: 10, fontSize: 12, padding: "8px 12px", borderRadius: 6,
            color: promoMsg.ok ? DS.success : DS.danger,
            background: promoMsg.ok ? "rgba(75,184,122,0.08)" : "rgba(224,70,70,0.08)",
            border: `1px solid ${promoMsg.ok ? "rgba(75,184,122,0.2)" : "rgba(224,70,70,0.2)"}`,
          }}>{promoMsg.text}</div>
        )}
      </div>

      {/* Purchase history */}
      <div style={card}>
        <div style={sLabel}>Purchase History</div>
        {transactions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "28px 0", border: `1px dashed ${DS.borderFaint}`, borderRadius: 8 }}>
            <div style={{ fontSize: 22, marginBottom: 8, opacity: 0.3 }}>♕</div>
            <div style={{ fontSize: 12, color: DS.textFaint }}>No purchases yet</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${DS.borderFaint}` }}>
                {["Date", "Item", "Amount"].map(h => (
                  <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 10, color: DS.textFaint, fontFamily: "'Cinzel', serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id} style={{ borderBottom: `1px solid ${DS.borderFaint}` }}>
                  <td style={{ padding: "8px 8px", color: DS.textMuted, fontSize: 12 }}>{new Date(tx.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding: "8px 8px", color: DS.text, fontWeight: 500 }}>{tx.description || tx.type}</td>
                  <td style={{ padding: "8px 8px", fontWeight: 700, color: DS.danger }}>${Math.abs(tx.amountCents / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Bank payout account */}
      <div style={card}>
        <div style={sLabel}>Bank Payout Account</div>
        {!connectStatus?.connected ? (
          <>
            <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
              Connect a bank account to receive tournament winnings directly.
              ID verification is handled securely by Stripe.
            </div>
            <button className="btn btn-primary cinzel"
              style={{ fontSize: 11, letterSpacing: "0.06em" }}
              onClick={handleConnect} disabled={connectLoading}>
              {connectLoading ? "Loading…" : "Connect Bank Account"}
            </button>
          </>
        ) : connectStatus.payoutsEnabled ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22, color: DS.success }}>✓</span>
            <div>
              <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, color: DS.success }}>Payouts Enabled</div>
              <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 3 }}>
                Tournament winnings will be transferred directly to your bank.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, color: DS.warning, marginBottom: 14, lineHeight: 1.6 }}>
              Onboarding incomplete — finish setup to enable payouts.
            </div>
            <button className="btn btn-gold cinzel"
              style={{ fontSize: 11, letterSpacing: "0.06em" }}
              onClick={handleConnect} disabled={connectLoading}>
              {connectLoading ? "Loading…" : "Finish Setup"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── HISTORY ───────────────────────────────────────────────────────────────────

function HistorySection({ user }) {
  const [games, setGames]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [replayGame, setReplayGame] = useState(null); // game object to replay

  useEffect(() => {
    if (!user?.token) { setLoading(false); return; }
    getUserGames(user.token)
      .then(data => { if (Array.isArray(data)) setGames(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  function ResultBadge({ result }) {
    const s = result === "win"
      ? { label: "W", color: DS.success, bg: "rgba(75,184,122,0.15)" }
      : result === "loss"
      ? { label: "L", color: DS.danger,  bg: "rgba(224,70,70,0.15)" }
      : { label: "D", color: DS.textMuted, bg: DS.elevated };
    return (
      <span className="cinzel" style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: 6,
        fontSize: 10, fontWeight: 700,
        color: s.color, background: s.bg,
      }}>{s.label}</span>
    );
  }

  const wins   = games.filter(g => g.result === "win").length;
  const losses = games.filter(g => g.result === "loss").length;
  const draws  = games.filter(g => g.result === "draw").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Summary row */}
      {games.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {[
            { label: "Wins",   value: wins,   color: DS.success },
            { label: "Losses", value: losses, color: DS.danger  },
            { label: "Draws",  value: draws,  color: DS.textMuted },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: DS.elevated, border: `1px solid ${DS.borderFaint}`,
              borderRadius: DS.radius, padding: "14px 0", textAlign: "center",
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
              <div className="cinzel" style={{ fontSize: 10, color: DS.textFaint, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Game list */}
      <div style={card}>
        <div style={sLabel}>Online Games</div>
        {loading ? (
          <div style={{ fontSize: 13, color: DS.textMuted }}>Loading…</div>
        ) : games.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "36px 0",
            border: `1px dashed ${DS.borderFaint}`, borderRadius: 8,
          }}>
            <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.25 }}>⚔</div>
            <div className="cinzel" style={{ fontSize: 11, color: DS.textFaint, letterSpacing: "0.06em" }}>
              No online games yet
            </div>
            <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 6 }}>
              Play an online match — it'll appear here
            </div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${DS.borderFaint}` }}>
                {["", "Opponent", "Side", "Moves", "Date", ""].map(h => (
                  <th key={h} style={{
                    padding: "6px 8px", textAlign: "left",
                    fontSize: 10, color: DS.textFaint,
                    fontFamily: "'Cinzel', serif", textTransform: "uppercase", letterSpacing: "0.08em",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {games.map(g => (
                <tr key={g.id} style={{ borderBottom: `1px solid ${DS.borderFaint}` }}>
                  <td style={{ padding: "9px 8px" }}><ResultBadge result={g.result} /></td>
                  <td style={{ padding: "9px 8px", color: DS.text, fontWeight: 500, fontSize: 13 }}>
                    {g.opponent || "Unknown"}
                  </td>
                  <td style={{ padding: "9px 8px", fontSize: 20, lineHeight: 1 }}>
                    {g.playedAs === "white" ? "♔" : "♚"}
                  </td>
                  <td style={{ padding: "9px 8px", color: DS.textMuted, fontSize: 12 }}>
                    {g.moveCount}
                  </td>
                  <td style={{ padding: "9px 8px", color: DS.textMuted, fontSize: 12 }}>
                    {new Date(g.playedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: "9px 8px" }}>
                    <button
                      onClick={() => setReplayGame(g)}
                      style={{
                        background: "none", border: `1px solid ${DS.borderFaint}`,
                        borderRadius: 6, padding: "3px 8px", cursor: "pointer",
                        fontSize: 10, color: DS.textMuted, fontFamily: "inherit",
                      }}
                    >▶ Replay</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {replayGame && (
        <ReplayModal game={replayGame} user={user} onClose={() => setReplayGame(null)} />
      )}
    </div>
  );
}

// ── REPLAY MODAL ──────────────────────────────────────────────────────────────

const SQ = 52;
const LIGHT = "#F0D9B5";
const DARK  = "#B58863";

function buildStates(moves) {
  const states = [getInitialGame("pvp")];
  for (const mv of moves) {
    const prev = states[states.length - 1];
    try {
      const { board: nb, capturedType } = applyMove(prev.board, mv.from, mv.type, prev.turn, mv.to, mv.promo || null);
      states.push(finalizeMove(prev, prev.turn, nb, capturedType, mv.from, mv.to, mv.type, mv.promo || null));
    } catch { break; }
  }
  return states;
}

function ReplayBoard({ gameState }) {
  const { board, lastMove } = gameState;
  const squares = Array.from({ length: 64 }, (_, i) => i);
  return (
    <div style={{
      display: "inline-grid", gridTemplateColumns: `repeat(8, ${SQ}px)`,
      border: "2px solid #0A0913", borderRadius: 4, overflow: "hidden",
    }}>
      {squares.map(sq => {
        const row = Math.floor(sq / 8), col = sq % 8;
        const isDark = (row + col) % 2 === 1;
        const piece  = getPieceAt(sq, board);
        const isLast = lastMove && (sq === lastMove.from || sq === lastMove.to);
        const isW    = isWP(sq, board);
        let bg = isDark ? DARK : LIGHT;
        if (isLast) bg = isDark ? "#aabe51" : "#cdd16f";
        const style = getPieceStyle("classic", "classic", isW);
        return (
          <div key={sq} style={{ width: SQ, height: SQ, background: bg,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            {piece && <span style={{ fontSize: SQ * 0.7, lineHeight: 1, ...style }}>{piece}</span>}
          </div>
        );
      })}
    </div>
  );
}

function ReplayModal({ game, user, onClose }) {
  const [states, setStates] = useState(null);
  const [idx, setIdx]       = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (!user?.token) return;
    setLoading(true);
    getGameMoves(user.token, game.id)
      .then(data => {
        if (!data?.moves?.length) { setError("No move data available for this game."); return; }
        setStates(buildStates(data.moves));
        setIdx(0);
      })
      .catch(() => setError("Failed to load game."))
      .finally(() => setLoading(false));
  }, [game.id, user]);

  const go = useCallback((delta) => {
    setIdx(i => Math.max(0, Math.min((states?.length || 1) - 1, i + delta)));
  }, [states]);

  useEffect(() => {
    const handler = e => {
      if (e.key === "ArrowLeft")  go(-1);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "Escape")     onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go, onClose]);

  const total = states ? states.length - 1 : 0;
  const moveNum = idx;

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(8,7,18,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 800, backdropFilter: "blur(6px)",
    }}>
      <div style={{
        background: DS.surface, border: `1px solid ${DS.borderFaint}`,
        borderRadius: 16, padding: "24px 24px 20px",
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
        maxWidth: "calc(100vw - 32px)",
      }}>
        {/* Header */}
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="cinzel" style={{ fontSize: 12, fontWeight: 700, color: DS.text }}>
              {game.opponent ? `vs ${game.opponent}` : "Game Replay"}
            </div>
            <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 2 }}>
              Played as {game.playedAs} · {new Date(game.playedAt).toLocaleDateString()}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 18, color: DS.textMuted, lineHeight: 1, padding: 4,
          }}>✕</button>
        </div>

        {loading && <div style={{ color: DS.textFaint, fontSize: 13, padding: "32px 0" }}>Loading moves…</div>}
        {error   && <div style={{ color: DS.danger, fontSize: 13, padding: "16px 0" }}>{error}</div>}

        {states && (
          <>
            <ReplayBoard gameState={states[idx]} />

            {/* Move counter */}
            <div style={{ fontSize: 11, color: DS.textMuted }}>
              Move <span style={{ fontWeight: 700, color: DS.text }}>{moveNum}</span> / {total}
              {idx === total && (
                <span style={{ marginLeft: 8, color: game.result === "win" ? DS.success : game.result === "loss" ? DS.danger : DS.textMuted }}>
                  {game.result === "win" ? " · You won" : game.result === "loss" ? " · You lost" : " · Draw"}
                </span>
              )}
            </div>

            {/* Controls */}
            <div style={{ display: "flex", gap: 8 }}>
              {[
                ["⟨⟨", () => setIdx(0)],
                ["⟨",  () => go(-1)],
                ["⟩",  () => go(1)],
                ["⟩⟩", () => setIdx(total)],
              ].map(([label, fn]) => (
                <button key={label} onClick={fn} style={{
                  width: 40, height: 36, background: DS.elevated,
                  border: `1px solid ${DS.borderFaint}`, borderRadius: 8,
                  cursor: "pointer", fontSize: 14, color: DS.text,
                  fontFamily: "inherit",
                }}>{label}</button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: DS.textFaint }}>← → arrow keys to navigate</div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const card      = { background: DS.surface, border: `1px solid ${DS.borderFaint}`, borderRadius: DS.radius, padding: "18px 20px" };
const sLabel    = { fontFamily: "'Cinzel', Georgia, serif", fontSize: 10, fontWeight: 700, color: DS.textFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 };
const inputStyle = {
  width: "100%", padding: "10px 14px", boxSizing: "border-box",
  background: DS.elevated, border: `1px solid ${DS.border}`,
  borderRadius: 8, color: DS.text, fontSize: 13,
  outline: "none", fontFamily: "inherit",
};
